import { useState } from "react";
import { toast } from "sonner";
import { HardDrive, Loader2, RefreshCw, Eraser } from "lucide-react";
import { appsApi, errMsg, notifyError, fmtSize, fmtDate } from "@/lib/appsApi";

const LinkInfo = ({ o }) => {
  const l = o.link;
  if (!l) return <span className="text-amber-800" data-testid="apps-storage-unlinked">Not in this site's file list</span>;
  if (l.kind === "shared") return <span className="text-sky-800">Shared data file · {l.name}</span>;
  return (
    <span className={l.deleted ? "text-slate-500" : "text-ink"}>
      {l.name}{l.version ? ` · v${l.version}` : ""}{l.tab ? ` · ${l.tab}` : ""}
      {l.deleted ? " · deleted from the list" : " · in use"}
    </span>
  );
};

const warning = (o) => {
  const l = o.link;
  if (!l) return "This stored file isn't linked to any entry in this site's file list. If it belongs to the live site or another copy of this site, emptying it will break that download.";
  if (l.kind === "file" && !l.deleted) return `"${l.name}" is still in the file list. Emptying it removes that entry too, and testers can no longer download it.`;
  return "This file was already deleted from the list, so emptying it only frees the space.";
};

const StorageRow = ({ o, onEmptied }) => {
  const [busy, setBusy] = useState(false);
  const empty = async () => {
    if (!window.confirm(`Empty this stored file (${fmtSize(o.size)})?\n\n${warning(o)}\n\nThis can't be undone.`)) return;
    setBusy(true);
    try {
      const r = await appsApi.post("/admin/storage/empty", { path: o.path });
      toast.success(`Emptied ${fmtSize(o.size)} from storage${r?.data?.removed_entries ? " and removed its file list entry" : ""}`);
      onEmptied(o.path);
    } catch (err) {
      notifyError(errMsg(err, "Emptying the stored file"));
      setBusy(false);
    }
  };
  const shared = o.link?.kind === "shared";
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4" data-testid="apps-storage-row">
      <div className="min-w-0">
        <p className="text-sm"><LinkInfo o={o} /></p>
        <p className="break-all font-mono text-[11px] text-slate-500">{o.path}</p>
        <p className="font-mono text-[11px] text-slate-600">{fmtSize(o.size)} · stored {fmtDate(o.last_modified)}</p>
      </div>
      <button onClick={empty} disabled={busy || shared} title={shared ? "Replace shared data files in the Shared data files section instead" : "Empty this file in storage"}
        className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 sm:self-center" data-testid={`apps-storage-empty-${o.path.split("/").pop()}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eraser className="h-3.5 w-3.5" />}Empty
      </button>
    </li>
  );
};

export const StorageBrowser = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await appsApi.get("/admin/storage");
      setData({ objects: Array.isArray(r?.data?.objects) ? r.data.objects : [], total: r?.data?.total_size ?? 0, truncated: !!r?.data?.truncated });
    } catch (err) {
      notifyError(errMsg(err, "Listing file storage"));
    } finally {
      setBusy(false);
    }
  };
  const emptied = (path) => {
    setData((d) => ({ ...d, objects: d.objects.filter((o) => o.path !== path), total: d.total - (d.objects.find((o) => o.path === path)?.size ?? 0) }));
    onChanged();
  };
  return (
    <section className="mt-10 rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8" data-testid="apps-storage-browser">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><HardDrive className="h-5 w-5" /></span>
          <div>
            <h3 className="font-display text-xl font-semibold text-ink">File storage</h3>
            <p className="mt-1 max-w-xl text-sm text-slate-600">Everything this site has stored. Storage can't delete files outright, so <strong>Empty</strong> overwrites a file with nothing, which frees its space; emptied files are hidden here.</p>
          </div>
        </div>
        <button onClick={load} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink hover:border-sky-600 hover:text-sky-700 disabled:opacity-60" data-testid="apps-storage-load">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{data ? "Refresh" : "Show stored files"}
        </button>
      </div>
      {data && (
        <>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500" data-testid="apps-storage-summary">{data.objects.length} file{data.objects.length === 1 ? "" : "s"} · {fmtSize(data.total)}</p>
          {data.truncated && <p className="mt-2 text-xs font-medium text-amber-800">Storage only lists the first 1,000 files, so some may not be shown.</p>}
          {data.objects.length === 0 ? <p className="mt-3 text-sm text-slate-600" data-testid="apps-storage-empty-state">Nothing stored.</p> : (
            <ul className="mt-2 max-h-[32rem] divide-y divide-slate-100 overflow-y-auto pr-1" data-testid="apps-storage-list">
              {data.objects.map((o) => <StorageRow key={o.path} o={o} onEmptied={emptied} />)}
            </ul>
          )}
        </>
      )}
    </section>
  );
};
